<template>
  <Modal
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    max-width="max-w-[560px]"
    @close="emit('update:modelValue', false)"
  >
    <template #header>
      <h3 class="text-lg font-bold text-textPrimary">Critérios da aprovação automática</h3>
    </template>

    <div class="text-left text-sm text-textPrimary" data-testid="auto-approval-criteria-modal">
      <p class="mb-3 text-neutral50" data-testid="auto-approval-criteria-intro">
        {{ introText }}
      </p>

      <!--
        Vetor real avaliado pelo motor (§6.4), quando a trilha o devolve em
        `newValues.criteria` do evento CONTAINER_AUTO_APPROVED.
      -->
      <ul v-if="criteria.length > 0" class="flex flex-col gap-2">
        <li
          v-for="(criterion, index) in criteria"
          :key="criterion.key || index"
          class="flex items-start gap-2"
          :data-testid="`auto-approval-criterion-${index}`"
        >
          <span
            class="mt-0.5 text-xs font-bold"
            :class="criterion.passed === false ? 'text-negative' : 'text-sucess'"
            aria-hidden="true"
          >
            {{ criterion.passed === false ? '✕' : '✓' }}
          </span>
          <span>
            {{ criterion.label || criterion.key }}
            <span v-if="criterion.detail" class="block text-xs text-neutral50">
              {{ criterion.detail }}
            </span>
          </span>
        </li>
      </ul>

      <!-- Sem vetor: os seis critérios da política como texto, com a nota de ressalva. -->
      <template v-else>
        <ul class="flex flex-col gap-2" data-testid="auto-approval-criteria-fallback">
          <li
            v-for="(text, index) in AUTO_APPROVAL_CRITERIA_FALLBACK"
            :key="index"
            class="flex items-start gap-2"
          >
            <span class="mt-0.5 text-xs font-bold text-neutral50" aria-hidden="true">•</span>
            <span>{{ text }}</span>
          </li>
        </ul>
        <p class="mt-3 text-xs text-neutral50">{{ AUTO_APPROVAL_CRITERIA_FALLBACK_NOTE }}</p>
      </template>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <OutlineButton
          text="Fechar"
          width="120px"
          height="40px"
          data-testid="auto-approval-criteria-close"
          @click="emit('update:modelValue', false)"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
/**
 * Fase 2 do fluxo de auditoria (spec 2026-09-11 §6.3/§6.4) — "Ver critérios" do banner de
 * aprovação automática. Mostra o vetor avaliado pelo motor quando a trilha o devolve; sem
 * ele, os seis critérios da política como texto estático.
 */
import { computed } from 'vue'
import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import {
  AUTO_APPROVAL_CRITERIA_FALLBACK,
  AUTO_APPROVAL_CRITERIA_FALLBACK_NOTE
} from '@/constants/ds-attendance-auto-approval.constants'
import type { DSAttendanceAutoApprovalCriterion } from '@/Network/Types/Responses/DsAttendanceAuditResponses'

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    criteria?: DSAttendanceAutoApprovalCriterion[]
  }>(),
  { criteria: () => [] }
)

const emit = defineEmits<{
  'update:modelValue': [open: boolean]
}>()

/**
 * O vetor da trilha pode trazer um critério com `passed: false` (registro aprovado por uma
 * política anterior, critério que deixou de valer). Afirmar "todos os critérios passaram" com
 * um ✕ na lista logo abaixo contradiz o que o auditor está vendo.
 */
const allCriteriaPassed = computed(() =>
  props.criteria.every((criterion) => criterion.passed !== false)
)

const introText = computed(() =>
  allCriteriaPassed.value
    ? 'O registro foi aprovado sem passar pela auditoria porque todos os critérios abaixo passaram.'
    : 'O registro foi aprovado sem passar pela auditoria. Veja abaixo os critérios avaliados.'
)
</script>
